(() => {
  'use strict';

  if (window.OrbintFreeGift) return;

  const configElement = document.getElementById('orbint-free-gift-config');
  if (!configElement) return;

  let config;

  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('[Orbint free gift] Invalid configuration.', error);
    return;
  }

  const qualifyingVariantIds = new Set(
    (config.qualifyingVariantIds || []).map(Number).filter(Number.isFinite)
  );
  const promotionEnabled = config.promotionEnabled !== false;
  const giftVariantId = Number(config.giftVariantId);
  const propertyName = config.propertyName || '_orbint_free_gift';
  const propertyValue = String(config.propertyValue || 'true');
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;
  const addUrl = `${root}cart/add.js`;
  const updateUrl = `${root}cart/update.js`;
  const originalFetch = window.fetch.bind(window);
  const cartMutationPattern = /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:[?#]|$)/;
  const declinedStorageKey = `orbint_free_gift_declined_${giftVariantId || 'campaign'}`;

  let reconcilePromise = null;
  let timer = null;
  let reconcilePending = false;
  let retryAfter = 0;
  let lastManagedGiftQuantity = null;
  let giftDeclinedInMemory = false;
  let managedGiftKeys = new Set();
  let managedGiftIndexes = new Set();

  if (
    promotionEnabled &&
    (!qualifyingVariantIds.size ||
      !Number.isFinite(giftVariantId) ||
      giftVariantId <= 0)
  ) {
    console.warn(
      '[Orbint free gift] No qualifying variants or gift variant were configured.'
    );
    return;
  }

  function hasGiftMarker(item) {
    return String(item.properties?.[propertyName]) === propertyValue;
  }

  function isManagedGift(item) {
    return Number(item.variant_id) === giftVariantId && hasGiftMarker(item);
  }

  function rememberManagedGiftState(cart) {
    managedGiftKeys = new Set();
    managedGiftIndexes = new Set();

    cart.items.forEach((item, index) => {
      if (!isManagedGift(item)) return;
      managedGiftKeys.add(item.key);
      managedGiftIndexes.add(index + 1);
    });
  }

  function controlBelongsToManagedGift(element) {
    const row = element?.closest?.('.cart-item');
    const input =
      row?.querySelector?.('[data-quantity-variant-id]') ||
      element?.closest?.('[data-quantity-variant-id]');

    if (!input || Number(input.dataset.quantityVariantId) !== giftVariantId) {
      return false;
    }

    const lineKey = input.dataset.quantityLineKey;
    const lineIndex = Number(input.dataset.index);

    if (lineKey && managedGiftKeys.size) return managedGiftKeys.has(lineKey);
    if (Number.isFinite(lineIndex) && managedGiftIndexes.size) {
      return managedGiftIndexes.has(lineIndex);
    }

    return lastManagedGiftQuantity > 0 || managedGiftKeys.size === 0;
  }

  function recordGiftDecline() {
    setGiftDeclined();
    lastManagedGiftQuantity = 0;
    managedGiftKeys = new Set();
    managedGiftIndexes = new Set();
  }

  function giftWasDeclined() {
    if (giftDeclinedInMemory) return true;

    try {
      return window.localStorage.getItem(declinedStorageKey) === 'true';
    } catch (_error) {
      return false;
    }
  }

  function setGiftDeclined() {
    giftDeclinedInMemory = true;

    try {
      window.localStorage.setItem(declinedStorageKey, 'true');
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes. The current page
      // still respects the in-memory quantity observation.
    }
  }

  function clearGiftDeclined() {
    giftDeclinedInMemory = false;

    try {
      window.localStorage.removeItem(declinedStorageKey);
    } catch (_error) {
      // No action is needed when storage is unavailable.
    }
  }

  function targetGiftQuantity(cart) {
    return cart.items.reduce((total, item) => {
      if (!qualifyingVariantIds.has(Number(item.variant_id))) return total;
      return total + Number(item.quantity || 0);
    }, 0);
  }

  function getRenderer() {
    return (
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification')
    );
  }

  function getSectionIds() {
    const renderer = getRenderer();
    const ids = [];

    if (typeof renderer?.getSectionsToRender === 'function') {
      for (const section of renderer.getSectionsToRender()) {
        if (section?.id) ids.push(section.id);
      }
    }

    for (const selector of [
      '[data-id][id="main-cart-items"]',
      '[data-id][id="main-cart-footer"]',
    ]) {
      const sectionId = document.querySelector(selector)?.dataset.id;
      if (sectionId) ids.push(sectionId);
    }

    if (document.getElementById('cart-icon-bubble')) {
      ids.push('cart-icon-bubble');
    }

    return [...new Set(ids)].slice(0, 5);
  }

  function mutationBody(payload) {
    const sectionIds = getSectionIds();

    if (sectionIds.length) {
      payload.sections = sectionIds;
      payload.sections_url = window.location.pathname + window.location.search;
    }

    return JSON.stringify(payload);
  }

  async function request(url, payload) {
    const response = await originalFetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: mutationBody(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || data.message || 'Cart request failed.');
    }

    return data;
  }

  function replaceReturnedSections(data) {
    if (!data?.sections) return;

    for (const [sectionId, html] of Object.entries(data.sections)) {
      if (!html) continue;

      const current = document.getElementById(`shopify-section-${sectionId}`);
      if (!current) continue;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.getElementById(`shopify-section-${sectionId}`);

      if (replacement) current.innerHTML = replacement.innerHTML;
    }
  }

  function refreshCartUi(data) {
    const renderer = getRenderer();
    data.id ||= giftVariantId;

    const rendererSections =
      typeof renderer?.getSectionsToRender === 'function'
        ? renderer.getSectionsToRender()
        : [];
    const sectionsAreValid =
      data?.sections &&
      rendererSections.every((section) => {
        const html = data.sections[section.id];
        if (typeof html !== 'string') return false;
        if (!section.selector) return true;

        return Boolean(
          new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(section.selector)
        );
      });

    if (typeof renderer?.renderContents === 'function' && sectionsAreValid) {
      renderer.renderContents(data);
    } else {
      replaceReturnedSections(data);
    }

    document.dispatchEvent(
      new CustomEvent('orbint:free-gift-updated', {
        detail: { cart: data },
      })
    );
  }

  async function fetchCart() {
    const response = await originalFetch(cartUrl, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Unable to read the cart.');
    return response.json();
  }

  async function reconcile() {
    if (Date.now() < retryAfter) return;

    const cart = await fetchCart();
    rememberManagedGiftState(cart);
    const markedGiftLines = cart.items.filter(hasGiftMarker);

    if (!promotionEnabled) {
      clearGiftDeclined();
      if (!markedGiftLines.length) return;

      const cleanupUpdates = {};
      markedGiftLines.forEach((item) => {
        cleanupUpdates[item.key] = 0;
      });

      const cleanupData = await request(updateUrl, {
        updates: cleanupUpdates,
      });
      refreshCartUi(cleanupData);
      return;
    }

    const targetQuantity = targetGiftQuantity(cart);
    let managedGiftLines = cart.items.filter(isManagedGift);
    const managedQuantity = managedGiftLines.reduce(
      (total, item) => total + Number(item.quantity || 0),
      0
    );

    if (targetQuantity > 0 && managedGiftLines.length === 0) {
      if (lastManagedGiftQuantity > 0) {
        setGiftDeclined();
      }

      lastManagedGiftQuantity = 0;
      if (giftWasDeclined()) return;

      const data = await request(addUrl, {
        items: [
          {
            id: giftVariantId,
            quantity: targetQuantity,
            properties: { [propertyName]: propertyValue },
          },
        ],
      });

      const cartAfterAdd = await fetchCart();
      rememberManagedGiftState(cartAfterAdd);
      lastManagedGiftQuantity = targetQuantity;
      refreshCartUi(data);
      return;
    }

    if (targetQuantity === 0) {
      clearGiftDeclined();
      lastManagedGiftQuantity = 0;

      if (!managedGiftLines.length) return;
    }

    const updates = {};

    if (targetQuantity === 0) {
      managedGiftLines.forEach((item) => {
        updates[item.key] = 0;
      });
    } else {
      // The script enforces only a minimum. Quantities above the Bag Set count
      // are intentional paid purchases and must never be reduced or split.
      if (managedQuantity >= targetQuantity) {
        lastManagedGiftQuantity = managedQuantity;
        return;
      }

      const deficit = targetQuantity - managedQuantity;
      const firstGiftLine = managedGiftLines[0];
      updates[firstGiftLine.key] = firstGiftLine.quantity + deficit;
      lastManagedGiftQuantity = targetQuantity;
    }

    const data = await request(updateUrl, { updates });
    if (Array.isArray(data.items)) rememberManagedGiftState(data);
    refreshCartUi(data);
  }

  function scheduleReconcile(delay = 80) {
    reconcilePending = true;
    window.clearTimeout(timer);

    timer = window.setTimeout(() => {
      if (reconcilePromise) return;

      reconcilePending = false;
      reconcilePromise = reconcile()
        .catch((error) => {
          retryAfter = Date.now() + 30000;
          console.warn('[Orbint free gift]', error.message);
        })
        .finally(() => {
          reconcilePromise = null;
          if (reconcilePending) scheduleReconcile(0);
        });
    }, delay);
  }

  window.fetch = async (...args) => {
    const responsePromise = originalFetch(...args);
    const input = args[0];
    const url = typeof input === 'string' ? input : input?.url || '';
    const response = await responsePromise;

    if (response.ok && cartMutationPattern.test(url)) {
      // Let Dawn finish consuming its response and replacing cart markup before
      // changing gift lines. Running during Dawn's debounced quantity handler
      // can make its old line index point at a different product.
      scheduleReconcile(350);
    }

    return response;
  };

  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS.cartUpdate
  ) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleReconcile(350));
  }

  document.addEventListener('cart:updated', () => scheduleReconcile(350));
  document.addEventListener('cart:refresh', () => scheduleReconcile(350));
  document.addEventListener(
    'click',
    (event) => {
      const removeControl = event.target?.closest?.(
        'cart-remove-button, a[href*="/cart/change"]'
      );

      if (
        removeControl &&
        controlBelongsToManagedGift(removeControl)
      ) {
        recordGiftDecline();
      }
    },
    true
  );
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target?.closest?.('[data-quantity-variant-id]');

      if (
        input &&
        Number(input.value) === 0 &&
        controlBelongsToManagedGift(input)
      ) {
        recordGiftDecline();
      }
    },
    true
  );
  window.addEventListener('pageshow', () => scheduleReconcile(0));

  window.OrbintFreeGift = {
    version: '1.9.0',
    enabled: promotionEnabled,
    isDeclined: () => giftWasDeclined(),
    refresh: () => scheduleReconcile(0),
    restore: () => {
      clearGiftDeclined();
      lastManagedGiftQuantity = null;
      scheduleReconcile(0);
    },
  };

  scheduleReconcile(0);
})();
